// Super Admin › Exports institutions: the nightly per-institution export
// (Excel workbooks + document mirror), with manual runs, zip download,
// contents view and the Google Drive mirror status.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileSpreadsheet, RefreshCw, Play } from 'lucide-react';
import toast from 'react-hot-toast';
import { tenantExportService } from '@/services/tenantExportService';
import { getInstitutionsWithUserCounts, InstitutionWithCounts } from '@/services/superAdminUserService';
import { InstitutionSelector } from '@/shared/components/InstitutionSelector';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import type { BackupOverview, DriveStatus, TenantExportRun } from '@/types/tenant-export.types';
import { TenantExportRunsTable } from '../components/tenant-export/TenantExportRunsTable';
import { TenantExportManifestModal } from '../components/tenant-export/TenantExportManifestModal';
import { TenantExportDrivePanel } from '../components/tenant-export/TenantExportDrivePanel';
import { BackupOverviewPanel } from '../components/tenant-export/BackupOverviewPanel';

const POLL_MS = 5000;

const ALL_INSTITUTIONS = '*';

const errorMessage = (err: unknown, fallback: string): string => (err instanceof Error && err.message ? err.message : fallback);

export const TenantExportsPage: React.FC = () => {
  const [institutions, setInstitutions] = useState<InstitutionWithCounts[]>([]);
  const [filterTenantId, setFilterTenantId] = useState('');
  const [runs, setRuns] = useState<TenantExportRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [driveStatus, setDriveStatus] = useState<DriveStatus | null>(null);
  const [driveLoading, setDriveLoading] = useState(true);
  const [overview, setOverview] = useState<BackupOverview | null>(null);
  const [runTarget, setRunTarget] = useState<{ id: string; name: string } | null>(null);
  const [starting, setStarting] = useState(false);
  const [busyRunId, setBusyRunId] = useState<string | null>(null);
  const [manifestRun, setManifestRun] = useState<TenantExportRun | null>(null);

  const loadRuns = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      setRuns(await tenantExportService.listRuns(filterTenantId || undefined));
    } catch (err) {
      if (!silent) toast.error(errorMessage(err, 'Impossible de charger les exports'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filterTenantId]);

  const loadDrive = useCallback(async () => {
    setDriveLoading(true);
    try {
      const [status, summary] = await Promise.all([tenantExportService.driveStatus(), tenantExportService.overview()]);
      setDriveStatus(status);
      setOverview(summary);
    } catch (err) {
      toast.error(errorMessage(err, 'Statut des sauvegardes indisponible'));
    } finally {
      setDriveLoading(false);
    }
  }, []);

  useEffect(() => {
    getInstitutionsWithUserCounts()
      .then((list) => setInstitutions(list.filter((i) => i.status === 'ACTIVE')))
      .catch(() => toast.error('Impossible de charger les institutions'));
    void loadDrive();
  }, [loadDrive]);

  useEffect(() => { void loadRuns(); }, [loadRuns]);

  // Follow runs in progress without the user refreshing.
  const hadRunning = useRef(false);
  useEffect(() => {
    const running = runs.some((r) => r.status === 'RUNNING' || r.status === 'PENDING');
    if (hadRunning.current && !running) void loadDrive(); // a run just finished: refresh the overview
    hadRunning.current = running;
    if (!running) return;
    const timer = setInterval(() => { void loadRuns(true); }, POLL_MS);
    return () => clearInterval(timer);
  }, [runs, loadRuns, loadDrive]);

  const handleStart = async (): Promise<void> => {
    if (!runTarget) return;
    setStarting(true);
    try {
      if (runTarget.id === ALL_INSTITUTIONS) {
        const { tenantIds } = await tenantExportService.startRunAll();
        toast.success(`Export de ${tenantIds.length} institution(s) lancé — une par une, en arrière-plan`);
      } else {
        await tenantExportService.startRun(runTarget.id);
        toast.success(`Export de ${runTarget.name} lancé — il continue en arrière-plan`);
      }
      setRunTarget(null);
      await loadRuns(true);
    } catch (err) {
      toast.error(errorMessage(err, "Impossible de lancer l'export"));
    } finally {
      setStarting(false);
    }
  };

  const handleDownload = async (run: TenantExportRun): Promise<void> => {
    setBusyRunId(run.id);
    try {
      await tenantExportService.downloadZip(run.id, `NEXEDU-export-${run.tenantName}.zip`);
    } catch (err) {
      toast.error(errorMessage(err, 'Téléchargement impossible'));
    } finally {
      setBusyRunId(null);
    }
  };

  const handleDriveSync = async (run: TenantExportRun): Promise<void> => {
    setBusyRunId(run.id);
    try {
      const result = await tenantExportService.syncToDrive(run.id);
      toast.success(`Drive : ${result.workbooks} classeurs, documents +${result.documentsCopied}`);
      await loadDrive();
    } catch (err) {
      toast.error(errorMessage(err, 'Copie Drive impossible'));
    } finally {
      setBusyRunId(null);
    }
  };

  const selectedInstitution = institutions.find((i) => i.id === filterTenantId);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <FileSpreadsheet className="text-emerald-600" size={26} /> Exports institutions
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Chaque nuit (04:00 UTC), toutes les données de chaque institution en classeurs Excel + un miroir de ses documents.
            Conservation : 31 derniers jours + le 1er de chaque mois pour toujours.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { void loadRuns(); void loadDrive(); }}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
          <button
            onClick={() => setRunTarget(selectedInstitution
              ? { id: selectedInstitution.id, name: selectedInstitution.name }
              : { id: ALL_INSTITUTIONS, name: 'toutes les institutions' })}
            disabled={institutions.length === 0}
            title={selectedInstitution ? `Exporter ${selectedInstitution.name} maintenant` : 'Exporter toutes les institutions actives, une par une'}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            <Play size={16} /> {selectedInstitution ? 'Exporter maintenant' : 'Exporter toutes les institutions'}
          </button>
        </div>
      </div>

      <BackupOverviewPanel overview={overview} />

      <TenantExportDrivePanel status={driveStatus} loading={driveLoading} />

      <div className="max-w-md">
        <label className="mb-1 block text-sm font-medium text-gray-700">Institution</label>
        <InstitutionSelector
          value={filterTenantId}
          onChange={(id) => setFilterTenantId(id)}
          institutions={institutions}
          placeholder="Toutes les institutions"
        />
      </div>

      <TenantExportRunsTable
        runs={runs}
        loading={loading}
        driveConfigured={driveStatus?.configured ?? false}
        busyRunId={busyRunId}
        onViewManifest={setManifestRun}
        onDownload={(run) => { void handleDownload(run); }}
        onDriveSync={(run) => { void handleDriveSync(run); }}
      />

      {manifestRun && <TenantExportManifestModal run={manifestRun} onClose={() => setManifestRun(null)} />}

      <ConfirmDialog
        isOpen={runTarget !== null}
        title="Exporter maintenant ?"
        message={runTarget?.id === ALL_INSTITUTIONS
          ? `Chaque institution active sera exportée à son tour (Excel + documents), comme lors du passage automatique de la nuit. Cela peut prendre plusieurs minutes par institution et tourne en arrière-plan.`
          : runTarget ? `Toutes les données de ${runTarget.name} seront exportées en Excel et ses documents recopiés. L'opération dure de une à quelques minutes et tourne en arrière-plan.` : ''}
        confirmText="Lancer l'export"
        cancelText="Annuler"
        onConfirm={() => { void handleStart(); }}
        onCancel={() => setRunTarget(null)}
        loading={starting}
      />
    </div>
  );
};

export default TenantExportsPage;
